import { useEffect, useState } from "react";
import "./NotificationPortal.css";

interface NotificationProps {
  message: string;
  duration?: number;
  onClose?: () => void;
  position?: { x: number; y: number };
  align?: "left" | "right" | "center";
  minWidth?: number; // Nueva prop opcional
}

export const Notification = ({
  message,
  duration = 5000,
  onClose,
  position,
  align = "left",
  minWidth = 740, // Valor por defecto
}: NotificationProps) => {
  const [isVisible, setIsVisible] = useState(true);
  const [isClosing, setIsClosing] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  // Efecto para monitorear el ancho de ventana
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Efecto para auto-cierre
  useEffect(() => {
    if (duration > 0 && windowWidth >= minWidth) {
      // Solo si cumple el ancho mínimo
      const timer = setTimeout(() => {
        handleClose();
      }, duration);

      return () => clearTimeout(timer);
    }
  }, [duration, windowWidth, minWidth]);

  const handleClose = () => {
    if (isClosing) return;

    setIsClosing(true);
    setTimeout(() => {
      setIsVisible(false);
      onClose?.();
    }, 300);
  };

  // NO mostrar si la ventana es menor que minWidth
  if (!isVisible || windowWidth < minWidth) return null;

  // Determinar tipo de posición
  const hasCustomPosition = !!position;
  const positionClass = hasCustomPosition
    ? "custom-position"
    : "default-position";

  // Estilos inline
  const notificationStyle: React.CSSProperties = hasCustomPosition
    ? {
        position: "fixed",
        left: `${position.x}px`,
        top: `${position.y}px`,
        zIndex: 99999,
        maxWidth: "280px",
        transform: "translateY(-50%)",
      }
    : {
        position: "fixed",
        bottom: "60px",
        right: "70px",
        zIndex: 99999,
        maxWidth: "280px",
      };

  // Clase completa
  const containerClassName = `notification-container ${positionClass} ${
    isClosing ? "closing" : ""
  }`;

  return (
    <div
      className={containerClassName}
      style={notificationStyle}
      role="alert"
      aria-live="polite"
    >
      <div className="notification-content">
        <span className="notification-message">{message}</span>
        <button
          className="notification-close-btn"
          onClick={handleClose}
          aria-label="Cerrar notificación"
        >
          ×
        </button>
      </div>
    </div>
  );
};
